import type { CommandKeys } from '../types/CommandKeys';
import { getCanonicalDisplayOrder } from './getKeyboardShortcutString';

// Display friendly names
export const displayKeyName = (isApple: boolean, key: string): string => {
  switch (key) {
    case 'Meta':
      return isApple ? '⌘' : 'Ctrl';
    case 'Alt':
      return isApple ? '⌥' : 'Alt';
    case 'Ctrl':
    case 'Control':
      return isApple ? '⌃' : 'Ctrl';
    case 'Shift':
      return '⇧';
    case 'ArrowLeft':
      return '←';
    case 'ArrowRight':
      return '→';
    case 'ArrowUp':
      return '↑';
    case 'ArrowDown':
      return '↓';
    case 'Enter':
      return '↵';
    case ' ':
      return '␣';
    // Cross-platform Backspace glyph (U+232B). Without this branch, the
    // default `toUpperCase()` would render "BACKSPACE", which dwarfs every
    // other keycap and reads as a word, not a key.
    case 'Backspace':
      return '⌫';
    default:
      return key.toUpperCase();
  }
};

// Helper functions for validation
const MODIFIER_KEYS = ['Meta', 'Alt', 'Ctrl', 'Control', 'Shift'] as const;
export type ModifierKey = (typeof MODIFIER_KEYS)[number];

export const isModifierKey = (key: string): key is ModifierKey => {
  return MODIFIER_KEYS.includes(key as ModifierKey);
};

export const isValidNonModifierKey = (key: string): boolean => {
  // Allow A-Z
  if (/^[A-Za-z]$/.test(key)) return true;

  // Allow function keys (F1-F12)
  if (/^F[1-9]|F1[0-2]$/.test(key)) return true;

  // Allow arrow keys
  if (key.startsWith('Arrow')) return true;

  // Allow numbers
  if (/^[0-9]$/.test(key)) return true;

  // Allow punctuation
  if (/^[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`]$/.test(key)) return true;

  // Allow specific special keys
  const specialKeys = [
    ' ',
    'Enter',
    'Tab',
    'Backspace',
    'Delete',
    'Escape',
    'Home',
    'End',
    'PageUp',
    'PageDown',
    'Insert',
    'PrintScreen',
    'ScrollLock',
    'Pause',
    'ContextMenu',
  ];
  if (specialKeys.includes(key)) return true;

  return false;
};

export const validateShortcut = (keys: string[]): boolean => {
  const modifiers = keys.filter(isModifierKey);
  const nonModifiers = keys.filter((key) => !isModifierKey(key));

  // Check for max 2 modifiers
  if (modifiers.length > 2) return false;

  // Check for exactly 1 non-modifier key
  if (nonModifiers.length !== 1) return false;

  // Check if the non-modifier key is valid
  if (!isValidNonModifierKey(nonModifiers[0])) return false;

  return true;
};

// Shortcuts the browser or OS intercepts before our handlers see them — so a
// user remapping a command onto one of these would silently do nothing.
// Meta+C/V/X are intentionally NOT here: Finder binds them to
// copy/paste/cut selection, and the browser only consumes them inside text
// inputs (which we already opt out of via input focus).
export const RESERVED_SHORTCUTS = [
  // Browser shortcuts
  ['Meta', 'R'], // Refresh
  ['Meta', 'Shift', 'R'], // Hard refresh
  ['Meta', 'L'], // Focus address bar
  ['Meta', 'T'], // New tab
  ['Meta', 'Shift', 'T'], // Reopen closed tab
  ['Meta', 'W'], // Close tab
  ['Meta', 'Shift', 'W'], // Close window
  ['Meta', 'N'], // New window
  ['Meta', 'Shift', 'N'], // New incognito window
  ['Meta', 'Q'], // Quit browser
  ['Meta', 'S'], // Save
  ['Meta', 'P'], // Print
  ['Meta', 'H'], // Hide window
  ['Meta', 'M'], // Minimize window
  ['Meta', '`'], // Switch windows
  ['Meta', 'Tab'], // Switch applications
  ['Alt', 'Tab'], // Switch applications
  ['Alt', 'F4'], // Close window
  ['F5'], // Refresh
  ['F11'], // Fullscreen
  ['F12'], // Developer tools
  ['Escape'], // Close/cancel
  ['Tab'], // Focus next
  ['Shift', 'Tab'], // Focus previous
  ['Home'], // Navigation
  ['End'], // Navigation
  ['Delete'], // Delete
  ['Backspace'], // Backspace
  ['Insert'], // Insert
  ['PrintScreen'], // Screenshot
  ['ScrollLock'], // Scroll lock
  ['Pause'], // Pause
  ['ContextMenu'], // Context menu
  ['CapsLock'], // Caps lock
  // App: `Shift+?` opens the keyboard shortcuts help dialog and isn't
  // customizable, so block users from rebinding onto it.
  ['Shift', '?'],
] as const;

export const isReservedShortcut = (keys: string[]): boolean => {
  // Canonicalize input so modifier order doesn't matter — the input
  // component stores modifiers in canonical priority order (Control, Shift,
  // Alt, Meta), but the entries above are written in human-readable order.
  const canonicalKeys = getCanonicalDisplayOrder(keys as CommandKeys[]);
  return RESERVED_SHORTCUTS.some((reserved) => {
    if (reserved.length !== canonicalKeys.length) return false;
    const canonicalReserved = getCanonicalDisplayOrder(
      reserved as ReadonlyArray<CommandKeys>
    );
    return canonicalReserved.every(
      (key, index) => key === canonicalKeys[index]
    );
  });
};
